import React, { useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { SUB_STATUSES, subMeta, MAIN_STATUSES, mainMeta } from "../lib/pipeline";
import { visibleJobSubStatuses } from "../lib/jobTypes";
import { Check, ChevronDown, Archive, User as UserIcon, AlertCircle } from "lucide-react";
import { useUiPref } from "../hooks/useUiPref";
import { useAuth } from "../context/AuthContext";

/**
 * Compute the wrapping-exit gate on the client so we can disable the
 * pipeline pill BEFORE the request fires (and show a checklist tooltip
 * explaining what's missing). Mirrors the backend's
 * `_enforce_wrapping_exit_gate` in pipeline.py — keep the two in sync.
 */
function wrappingGateMissing(job) {
  if (job.sub_status !== "wrapping" || job.main_status !== "job") return [];
  // Admin can turn the gate off from Settings → Job workflow. When
  // disabled, we skip every check so the pipeline pills stay clickable
  // just like a normal transition.
  if (typeof window !== "undefined" && window.__jobWorkflow?.require_wrapping_gate === false) {
    return [];
  }
  const missing = [];
  const photos = Array.isArray(job.photos) ? job.photos : [];
  if (photos.length === 0) missing.push("a photo of the finished job");
  let totalActual = 0;
  for (const it of job.job_items || []) {
    for (const inst of it.installations || []) {
      totalActual += Number(inst.actual_hours) || 0;
    }
  }
  if (totalActual <= 0) missing.push("total hours actually taken");
  const notes = (job.materials_used_notes || "").trim();
  const structuredMat = Array.isArray(job.materials_used) && job.materials_used.some((m) => (m?.name || "").trim());
  if (!notes && !structuredMat) missing.push("estimated material used");
  return missing;
}

/**
 * Compact horizontal stepper showing the CURRENT main-status's sub-pipeline.
 * Clicking a step advances the job to that sub_status.
 *
 * When main_status === "job", the visible steps are filtered by the
 * installations on the job. E.g. a simple colour change hides the design
 * stages; a business branding install shows them. Union of relevant stages
 * across all installations is shown.
 */
export default function StatusPipeline({ job, onChange, compact = false, inline = false, hideMain = false, vertical = false }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [openMain, setOpenMain] = useState(false);
  // Show installer name inline on each stage pill — driven by an
  // Appearance-level preference so admins can toggle it off if the extra
  // chip feels noisy. Only meaningful in vertical layout (side rail); the
  // horizontal row would blow up the pill width.
  const [showAssignees] = useUiPref("pipeline.showAssignees", false);
  const stageMap = job.stage_installers || {};

  const main = job.main_status || "job";
  const currentSub = job.sub_status || "";
  const allSteps = SUB_STATUSES[main] || [];
  const steps = main === "job"
    ? (() => {
        const visible = new Set(visibleJobSubStatuses(job));
        // Always show the currently-set status even if the installations don't
        // include it — prevents an orphaned status becoming invisible after an
        // install type is removed/edited.
        if (currentSub) visible.add(currentSub);
        return allSteps.filter((s) => visible.has(s.value));
      })()
    : allSteps;
  const currentIdx = steps.findIndex((s) => s.value === currentSub);
  // Client-side mirror of the backend's wrapping-exit gate. When the
  // job is in `wrapping` and any FORWARD stage is clicked, we disable
  // the pill + explain what's missing so the user isn't hit with a
  // 400 toast after clicking. Same rule as pipeline.py so the two
  // enforcement layers agree.
  const gateMissing = useMemo(() => wrappingGateMissing(job), [job]);
  const gateActive = gateMissing.length > 0;

  const setSub = async (sub, { override = false } = {}) => {
    if (busy || sub === currentSub) return;
    setBusy(true);
    try {
      const url = override ? `/jobs/${job.id}/sub-status?override=true` : `/jobs/${job.id}/sub-status`;
      const { data } = await api.put(url, { sub_status: sub });
      toast.success(`Stage → ${subMeta(main, sub).label}${override ? " (override)" : ""}`);
      onChange?.(data);
    } catch (err) {
      handleGateError(err, () => setSub(sub, { override: true }), "advance this job");
    } finally {
      setBusy(false);
    }
  };

  const setMain = async (nextMain, { override = false } = {}) => {
    if (busy || nextMain === main) { setOpenMain(false); return; }
    setBusy(true);
    try {
      const url = override ? `/jobs/${job.id}/advance?override=true` : `/jobs/${job.id}/advance`;
      const { data } = await api.post(url, { main_status: nextMain });
      toast.success(`Moved to ${mainMeta(nextMain).label}${override ? " (override)" : ""}`);
      onChange?.(data);
      setOpenMain(false);
    } catch (err) {
      handleGateError(err, () => setMain(nextMain, { override: true }), "advance this job");
    } finally {
      setBusy(false);
    }
  };

  // Shared error handler for the pipeline gates. If the backend returned a
  // structured `TERMS_NOT_SIGNED` code and the current user is an admin, we
  // show a two-button toast with "Override & continue" that re-fires the
  // same action with ?override=true. Non-admin users just see the message.
  function handleGateError(err, retryWithOverride, _actionLabel) {
    const detail = err?.response?.data?.detail;
    const isStructured = detail && typeof detail === "object" && detail.code;
    const canOverride = user?.role === "admin";
    if (isStructured && canOverride) {
      toast(
        (t) => (
          <div className="flex flex-col gap-2" data-testid="gate-override-toast">
            <div className="text-sm text-slate-100">{detail.message}</div>
            <div className="flex gap-2">
              <button
                onClick={() => { toast.dismiss(t); }}
                className="px-3 py-1 rounded-full text-xs bg-white/[0.06] text-slate-300 border border-white/10"
              >Cancel</button>
              <button
                onClick={() => { toast.dismiss(t); retryWithOverride(); }}
                data-testid="gate-override-continue"
                className="px-3 py-1 rounded-full text-xs bg-emerald-500 text-black font-semibold"
              >Override &amp; continue</button>
            </div>
          </div>
        ),
        { duration: 12000 }
      );
      return;
    }
    toast.error(typeof detail === "string" ? detail : detail?.message || "Failed");
  }

  return (
    <div className={inline ? "flex items-center gap-3 flex-wrap" : "space-y-2"} data-testid={`pipeline-${job.id}`}>
      {/* Main-status (Stage) selector — hidden in row bars so casual clicks
          can't accidentally move a record between Lead / Quote / Job. Only
          reachable from a record's Detail view where mistakes are rare and
          intentional. */}
      {!hideMain && (
      <div className="flex items-center gap-2">
        {!inline && <span className="text-[9px] uppercase tracking-widest text-slate-500">Stage</span>}
        <div className="relative">
          <button
            type="button"
            data-testid={`pipeline-main-${job.id}`}
            onClick={() => setOpenMain((v) => !v)}
            className={`flex items-center gap-1 pill pill-${mainMeta(main).colour} text-[10px] hover:opacity-80`}
          >
            {React.createElement(mainMeta(main).icon, { className: "w-3 h-3" })}
            {mainMeta(main).label}
            <ChevronDown className="w-3 h-3" />
          </button>
          {openMain && (
            <div className="absolute z-10 mt-1 left-0 rounded-2xl glass-strong border border-white/10 p-1 space-y-0.5 min-w-[140px]">
              {MAIN_STATUSES.map((m) => (
                <button
                  key={m.value}
                  data-testid={`pipeline-main-opt-${job.id}-${m.value}`}
                  onClick={() => setMain(m.value)}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-xs text-left ${m.value === main ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/[0.06]"}`}
                >
                  {React.createElement(m.icon, { className: "w-3 h-3" })}
                  {m.label}
                  {m.value === main && <Check className="w-3 h-3 ml-auto" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Sub-status stepper — horizontal by default, vertical when the
          consumer wants a compact right-rail layout (Job Detail's Overall
          Pipeline panel). Vertical mode swaps the flex direction and the
          connector orientation so the stage stack reads top-down. */}
      {/* Wrapping-exit gate checklist — surfaces the same requirements
          the backend enforces so the user can see WHY the next stage
          pills are greyed out. Only shows in non-inline / non-compact
          layouts so we don't crowd the row bars on the Jobs board. */}
      {gateActive && !inline && !compact && (
        <div
          data-testid={`pipeline-gate-checklist-${job.id}`}
          className="rounded-xl border border-amber-500/50 bg-amber-100 dark:bg-amber-500/[0.08] px-3 py-2 flex items-start gap-2"
        >
          <AlertCircle className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
          <div className="text-[11px] text-amber-900 dark:text-amber-100 leading-relaxed">
            <span className="font-semibold">Before advancing past wrapping:</span>
            <ul className="mt-0.5 space-y-0.5 list-disc list-inside marker:text-amber-600 dark:marker:text-amber-300/60">
              {gateMissing.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <div
        className={
          vertical
            ? "flex flex-col items-start gap-1"
            : "flex flex-wrap items-center gap-1"
        }
        data-testid={`pipeline-substeps-${job.id}`}
      >
        {steps.map((s, i) => {
          const isDone = currentIdx > -1 && i < currentIdx;
          const isCurrent = s.value === currentSub;
          const Icon = s.icon;
          const assignee = showAssignees && vertical && main === "job" ? stageMap[s.value] : null;
          // A forward step past `wrapping` is blocked when the gate has
          // unmet requirements. Reverts (past stages) and the current
          // stage itself always stay clickable.
          const isForward = currentIdx > -1 && i > currentIdx;
          const gated = gateActive && isForward;
          const tooltip = gated
            ? `Fill in ${gateMissing.join(", ")} on the job card first`
            : (assignee?.name ? `${s.label} · ${assignee.name}` : s.label);
          return (
            <React.Fragment key={s.value}>
              <button
                type="button"
                disabled={busy || gated}
                data-testid={`pipeline-sub-${job.id}-${s.value}`}
                onClick={() => !gated && setSub(s.value)}
                title={tooltip}
                aria-disabled={gated || busy}
                className={`flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] transition ${
                  vertical ? "w-full justify-start" : ""
                } ${
                  isCurrent
                    ? "bg-white text-black border-white font-semibold"
                    : isDone
                      ? "bg-white/[0.08] border-white/10 text-slate-300"
                      : gated
                        ? "bg-amber-50 dark:bg-white/[0.02] border-amber-400/60 dark:border-amber-400/25 text-amber-800 dark:text-amber-200/70 cursor-not-allowed"
                        : "bg-white/[0.02] border-white/10 text-slate-500 hover:text-slate-300 hover:border-white/20"
                } ${busy ? "opacity-60" : ""}`}
              >
                <Icon className="w-2.5 h-2.5" />
                {!compact && <span>{s.label}</span>}
                {gated && <AlertCircle className="w-2.5 h-2.5 text-amber-700 dark:text-amber-300" />}
                {assignee?.name && (
                  <span
                    data-testid={`pipeline-assignee-${job.id}-${s.value}`}
                    className={`ml-auto inline-flex items-center gap-1 pl-1.5 border-l text-[9px] ${
                      isCurrent
                        ? "border-black/20 text-black/70"
                        : "border-white/10 text-slate-400"
                    }`}
                  >
                    <UserIcon className="w-2.5 h-2.5" />
                    {assignee.name.split(" ")[0]}
                  </span>
                )}
              </button>
              {i < steps.length - 1 && (
                vertical ? (
                  <span className={`w-px h-2 ml-3 ${isDone ? "bg-white/40" : "bg-white/10"}`} />
                ) : (
                  <span className={`w-1.5 h-px ${isDone ? "bg-white/40" : "bg-white/10"}`} />
                )
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
